import * as path from "@tauri-apps/api/path";
import { isAbsolute, normalize } from "pathe";

import { env } from "../env/env";

const appEnv = env.appEnv ?? "local";

// Root
export const getBaseDir = async () => {
	if (env.isMobileApp) {
		return await path.documentDir();
	}

	return await path.homeDir();
};

export const getBaseDirEnum = () => {
	if (env.isMobileApp) {
		return path.BaseDirectory.Document;
	}
	return path.BaseDirectory.Home;
};

export const getMomentDirPath = async () => {
	const baseDir = await getBaseDir();
	return appEnv === "production"
		? path.join(baseDir, ".moment")
		: path.join(baseDir, `.moment-${appEnv || "local"}`);
};

export const getMomentGitConfigPath = async () => path.join(await getMomentDirPath(), ".gitconfig");

// Database
export const getDbBaseDir = async () => getMomentDirPath();
export const getMomentDbPath = async () => path.join(await getDbBaseDir(), "moment.db");

// Config
export const getConfigFilePath = async () => path.join(await getMomentDirPath(), "moment.json");

// Workspaces
export const getWorkspaceDirPath = async (workspaceName: string) =>
	path.join(await getMomentDirPath(), "workspaces", workspaceName);

export const getWorkspaceFilePath = async (workspaceName: string) =>
	path.join(await getWorkspaceDirPath(workspaceName), "workspace.json");

// Documents
export const getDocumentsDirPath = async () => path.join(await getMomentDirPath(), "documents");

export const getDocumentDirPath = async (documentId: string) =>
	path.join(await getDocumentsDirPath(), documentId);

export const getDocumentFilePath = async (documentPath: string) =>
	path.join(documentPath, "moment.yml");

export const getDocumentDotMomentPath = async (documentId: string) =>
	path.join(await getDocumentDirPath(documentId), ".moment");

export const getDocumentRequestHistoryDbPath = async (documentId: string) =>
	path.join(await getDocumentDotMomentPath(documentId), "request-history.sqlite");

export const getAttachmentsDirPath = async (documentId: string) =>
	path.join(await getDocumentDotMomentPath(documentId), "attachments");

export const getBasesDirPath = async (documentId: string) =>
	path.join(await getDocumentDotMomentPath(documentId), "bases");

export const getActiveDocumentBaseFilePath = async (documentId: string) =>
	path.join(await getBasesDirPath(documentId), "active-document-base.sqlite");

// Pages
export const getPageDirPath = async (documentPath: string, pageId: string, pagePath?: string) => {
	if (pagePath) {
		assertSafeRelativePath(pagePath, "pagePath");
		const absolutePagePath = await path.join(documentPath, pagePath);
		return path.dirname(absolutePagePath);
	}

	return path.join(documentPath, "pages", pageId);
};

/**
 * Validates that a relative path segment does not escape its parent directory.
 * Rejects absolute paths and paths containing ".." traversal segments.
 */
export function assertSafeRelativePath(segment: string, label: string): void {
	if (isAbsolute(segment)) {
		throw new Error(`${label} must be a relative path, got absolute: ${segment}`);
	}
	// Normalize to resolve any "." or ".." segments, then check for remaining ".."
	const normalized = normalize(segment);
	if (normalized.startsWith("..")) {
		throw new Error(`${label} must not traverse above its parent directory: ${segment}`);
	}
}

export const getPageFilePath = async (documentPath: string, pageId: string, pagePath?: string) => {
	// If an explicit path is provided, validate it stays within the document root
	if (pagePath) {
		assertSafeRelativePath(pagePath, "pagePath");
		return path.join(documentPath, pagePath);
	}
	// Otherwise, fall back to the default pages/{pageId}/index.md structure
	return path.join(await getPageDirPath(documentPath, pageId), "index.md");
};

export const getPageCanvasFilePath = async (documentPath: string, pageId: string) =>
	path.join(await getPageDirPath(documentPath, pageId), "index.json");

export const getPageStorageFilePath = async (documentPath: string, pageId: string) =>
	path.join(await getPageDirPath(documentPath, pageId), "pageStorage.json");

export const getPageRelatedFilesDirPath = async (documentPath: string, pageId: string) =>
	path.join(await getPageDirPath(documentPath, pageId), "related");

export const getPageRelatedFilePath = async (
	documentPath: string,
	pageId: string,
	filename: string
) => path.join(await getPageRelatedFilesDirPath(documentPath, pageId), filename);

/**
 * Extracts document ID from an absolute path within the Moment documents directory.
 *
 * @example
 * await parseDocumentIdFromPath("/Users/alex/.moment-local/documents/abc123/pages/page1/index.md")
 * // => "abc123"
 */
export async function parseDocumentIdFromPath(filePath: string): Promise<string | null> {
	const documentsDir = await getDocumentsDirPath();

	const normalizedPath = normalize(filePath);
	const normalizedDocumentsDir = normalize(documentsDir) + "/";

	if (!normalizedPath.startsWith(normalizedDocumentsDir)) {
		return null;
	}

	// Path after documents/ - e.g., "abc123/pages/page1/index.md"
	const relativePath = normalizedPath.slice(normalizedDocumentsDir.length);
	const documentId = relativePath.split("/")[0];

	return documentId || null;
}
